// frontend/src/pages/MergedMemberStatementPage.jsx
// Merged Member Statement — shows Firebase-saved records AND live-calculated records in ONE table.
// Each row is colour-coded by source:
//   🔒 Green  = 'saved'      — committed record read from Firestore
//   ⚡ Yellow = 'calculated' — computed on the fly (not yet saved)

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n, dp = 2) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}
function asYyyyMm(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
}

// ── Searchable member picker (same as MemberStatementPage) ────────────────────
function MemberSearch({ members, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = members.find(m => m.memberId === value);
  const displayLabel = selected ? `${selected.memberId} - ${selected.personalInfo?.fullName || ''}` : '';
  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? members.filter(m => {
        const q = query.toLowerCase();
        return m.memberId?.toLowerCase().includes(q) || (m.personalInfo?.fullName || '').toLowerCase().includes(q);
      })
    : members;

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(''); }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(m) { onChange(m.memberId); setQuery(''); setOpen(false); }
  function handleInputChange(e) { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }
  function handleFocus() { setQuery(''); setOpen(true); }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Type ID or name to search..."
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
        autoComplete="off"
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 8px rgba(0,0,0,0.12)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px', color: '#888', fontSize: '13px' }}>No members found</div>
            : filtered.map(m => (
                <div
                  key={m.id || m.memberId}
                  onMouseDown={() => handleSelect(m)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', background: m.memberId === value ? '#ede9fe' : 'white', fontWeight: m.memberId === value ? 600 : 400, borderBottom: '1px solid #f0f0f0' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                  onMouseLeave={e => e.currentTarget.style.background = m.memberId === value ? '#ede9fe' : 'white'}
                >
                  <span style={{ color: '#4b3ff6', fontWeight: 600 }}>{m.memberId}</span>{' — '}{m.personalInfo?.fullName || ''}{m.isLeft && <span style={{ marginLeft: 6, fontSize: 11, background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 10, fontWeight: 600 }}>Left</span>}
                </div>
              ))
          }
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const ROW_SAVED      = { background: '#f0fdf4', borderLeft: '3px solid #16a34a' }; // green
const ROW_CALCULATED = { background: '#fefce8', borderLeft: '3px solid #ca8a04' }; // yellow
const ROW_HOVER_SAVED      = { background: '#bfdbfe', borderLeft: '3px solid #16a34a' }; // soft blue over green
const ROW_HOVER_CALCULATED = { background: '#bfdbfe', borderLeft: '3px solid #ca8a04' }; // soft blue over yellow

export default function MergedMemberStatementPage() {
  const navigate   = useNavigate();
  const [searchParams] = useSearchParams();

  const [members,    setMembers]    = useState([]);
  const [memberId,   setMemberId]   = useState('');
  const [fromMonth,  setFromMonth]  = useState(asYyyyMm(new Date(new Date().getFullYear(), 0, 1)));
  const [toMonth,    setToMonth]    = useState(asYyyyMm(new Date()));
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [err,        setErr]        = useState('');

  // Pre-fill from query params (when navigating from MemberStatementPage)
  useEffect(() => {
    const pm = searchParams.get('memberId');
    const pf = searchParams.get('fromMonth');
    const pt = searchParams.get('toMonth');
    if (pm) setMemberId(pm);
    if (pf) setFromMonth(pf);
    if (pt) setToMonth(pt);
  }, [searchParams]);

  useEffect(() => { loadMembers(); }, []);

  async function loadMembers() {
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/members-for-statement`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const j = await r.json();
      if (j.success) setMembers(j.data?.members || []);
    } catch (e) { console.error(e); }
  }

  async function loadMerged() {
    if (!memberId) { setErr('Select a member'); return; }
    try {
      setErr(''); setLoading(true); setData(null);
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(
        `${API_URL}/reports/merged-statement/${memberId}?fromMonth=${fromMonth}&toMonth=${toMonth}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to load');
      setData(j.data);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }

  function exportCSV() {
    if (!data) return;
    const { member, statement, summary, fromMonth: fm, toMonth: tm } = data;

    let csv = '';
    csv += `Member ID,${member.memberId}\n`;
    csv += `Member Name,${member.fullName}\n`;
    csv += `From,${fm}\nTo,${tm}\n\n`;
    csv += [
      'Month','Source',
      'Shares','Shares Contri.',
      'Loan','Loan Int.','Loan Repayment','Add. Normal',
      'School Fee','School Int.','School Repayment','Add. School',
      'Emerg. Loan','Emerg. Int.','Emerg. Repayment','Add. Emergency',
      'Appl. Fees','Total Deductions'
    ].join(',') + '\n';

    for (const r of statement) {
      csv += [
        r.month, r.source,
        r.shares, r.sharesContribution,
        r.loanBalance, r.loanInterest, r.loanRepayment, r.additionalNormalDisbursed || 0,
        r.schoolFeesLoan, r.schoolFeesInterest, r.schoolFeesRepayment, r.additionalSchoolDisbursed || 0,
        r.emergencyLoan, r.emergencyLoanInterest, r.emergencyLoanRepayment, r.additionalEmergencyDisbursed || 0,
        r.applicationFees, r.totalDeductions
      ].join(',') + '\n';
    }

    csv += '\n';
    csv += [
      'TOTALS','',
      summary.totalShares, summary.totalSharesContributed,
      '', summary.totalLoanInterest, summary.totalLoanRepayments, '',
      '', summary.totalSchoolInterest, summary.totalSchoolRepayments, '',
      '', summary.totalEmergencyInterest, summary.totalEmergencyRepayments, '',
      summary.totalApplicationFees, summary.grandTotalDeductions
    ].join(',') + '\n';

    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `merged-statement-${memberId}-${fromMonth}-${toMonth}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%', background: '#f5f5f5', minHeight: '100vh' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ color: '#4b3ff6', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
          Merged Member Statement
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
          <span onClick={() => navigate('/admin/dashboard')} style={{ cursor: 'pointer', color: '#4b3ff6' }}>Dashboard</span>
          <span>›</span>
          <span>Member Statement</span>
          <span>›</span>
          <span style={{ color: '#333' }}>Merged View</span>
        </div>
      </div>

      <div className="container" style={{ background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        {/* ── Legend ── */}
        <div style={{ display: 'flex', gap: 20, marginBottom: 14, padding: '10px 14px', background: '#f9fafb', borderRadius: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#374151', fontSize: 13 }}>Legend:</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, background: '#f0fdf4', border: '2px solid #16a34a', borderRadius: 2 }}></span>
            <span>🔒 <b>Saved</b> — committed record from Firebase</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span style={{ display: 'inline-block', width: 14, height: 14, background: '#fefce8', border: '2px solid #ca8a04', borderRadius: 2 }}></span>
            <span>⚡ <b>Calculated</b> — computed live, not yet saved</span>
          </span>
        </div>

        {err && <div className="alert alert-danger" style={{ marginTop: 10 }}>{err}</div>}

        {/* ── Controls ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '240px auto auto 1fr', gap: 12, marginTop: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <label>Member *</label>
            <MemberSearch members={members} value={memberId} onChange={v => {
                setMemberId(v);
                setData(null);
                if (v) {
                  const selected = members.find(m => m.memberId === v);
                  if (selected?.isLeft && selected?.leftDate) {
                    // Handle both ISO string and Firestore timestamp formats
                    const ld = selected.leftDate._seconds
                      ? new Date(selected.leftDate._seconds * 1000)
                      : new Date(selected.leftDate);
                    if (!isNaN(ld.getTime())) {
                      const exitYear = ld.getFullYear();
                      const exitMonthNum = ld.getMonth() + 1;
                      const exitMM = `${exitYear}-${String(exitMonthNum).padStart(2, '0')}`;
                      // fromMonth = Jan of exit year; if exit is in Jan, go back to previous year
                      const fromYear = exitMonthNum <= 1 ? exitYear - 1 : exitYear;
                      setToMonth(exitMM);
                      setFromMonth(`${fromYear}-01`);
                    }
                  }
                }
              }} />
          </div>
          <div>
            <label>From (YYYY-MM) *</label>
            <input type="month" value={fromMonth} onChange={e => setFromMonth(e.target.value)} />
          </div>
          <div>
            <label>To (YYYY-MM) *</label>
            <input type="month" value={toMonth} onChange={e => setToMonth(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <button onClick={loadMerged} disabled={loading || !memberId}>
              {loading ? 'Loading...' : 'Load Merged'}
            </button>
            <button onClick={exportCSV} disabled={!data}>Export CSV</button>

          </div>
        </div>

        {/* ── Summary badge ── */}
        {data && data.member?.isLeft && (
          <div style={{
            background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8,
            padding: '12px 18px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12
          }}>
            <span style={{ fontSize: 20 }}>🗂️</span>
            <div>
              <strong style={{ color: '#92400e' }}>Archived Member — Left SACCO</strong>
              {data.member.leftDate && (
                <span style={{ color: '#78350f', fontSize: 13, marginLeft: 10 }}>
                  Left on {new Date(data.member.leftDate).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}
                </span>
              )}
              {data.member.leftReason && (
                <span style={{ color: '#78350f', fontSize: 13, marginLeft: 10 }}>
                  · Reason: {data.member.leftReason}
                </span>
              )}
              <div style={{ fontSize: 12, color: '#92400e', marginTop: 3 }}>
                Statement shown up to their exit month. All historical records are preserved.
              </div>
            </div>
          </div>
        )}
        {data && (
          <div style={{ marginTop: 14, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#dcfce7', color: '#15803d', fontSize: 13, fontWeight: 600 }}>
              🔒 {data.summary.savedCount} saved month{data.summary.savedCount !== 1 ? 's' : ''}
            </span>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#fef9c3', color: '#92400e', fontSize: 13, fontWeight: 600 }}>
              ⚡ {data.summary.calculatedCount} calculated month{data.summary.calculatedCount !== 1 ? 's' : ''}
            </span>
            <span style={{ padding: '4px 12px', borderRadius: 20, background: '#e0e7ff', color: '#3730a3', fontSize: 13, fontWeight: 600 }}>
              {data.member.memberId} — {data.member.fullName}
            </span>
          </div>
        )}

        {/* ── Merged table ── */}
        {data && (
          <div style={{ marginTop: 14, overflowX: 'auto' }}>
            <table className="table table-striped" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ paddingRight: 10 }}>Month</th>
                  <th style={{ paddingRight: 10, whiteSpace: 'nowrap' }}>Source</th>
                  <th style={{ paddingRight: 15 }}>Shares</th>
                  <th style={{ paddingRight: 15 }}>Shares Contri.</th>
                  <th style={{ paddingRight: 15 }}>Loan</th>
                  <th style={{ paddingRight: 15 }}>Loan Int.</th>
                  <th>Repayment</th>
                  <th>Add. Normal</th>
                  <th>School Fee</th>
                  <th>School Int.</th>
                  <th>School Repayment</th>
                  <th>Add. School</th>
                  <th>Emerg. Loan</th>
                  <th>Emerg. Int.</th>
                  <th>Emerg. Repayment</th>
                  <th>Add. Emergency</th>
                  <th>Appl. Fees</th>
                  <th>Total Deductions</th>
                </tr>
              </thead>
              <tbody>
                {data.statement.map((r, i) => {
                  const isSaved = r.source === 'saved';
                  const rowStyle = isSaved ? ROW_SAVED : ROW_CALCULATED;
                  return (
                    <tr key={i} style={rowStyle}
                      title={isSaved ? `Saved by ${r.savedBy || 'unknown'} on ${r.savedAt ? new Date(r.savedAt).toLocaleDateString() : '?'}` : 'Live calculation'}
                      onMouseEnter={e => e.currentTarget.style.background = '#bfdbfe'}
                      onMouseLeave={e => e.currentTarget.style.background = isSaved ? '#f0fdf4' : '#fefce8'}
                    >
                      <td style={{ paddingRight: 10, fontWeight: 600 }}>{r.month}</td>
                      <td style={{ paddingRight: 10, whiteSpace: 'nowrap', fontSize: 12 }}>
                        {isSaved
                          ? <span style={{ color: '#16a34a', fontWeight: 700 }}>🔒 Saved</span>
                          : <span style={{ color: '#ca8a04', fontWeight: 700 }}>⚡ Calc</span>
                        }
                      </td>
                      <td className="text-right" style={{ paddingRight: 15 }}>{fmt(r.shares, 0)}</td>
                      <td className="text-right" style={{ paddingRight: 15,
                        background: (r.sharesTransferAmount > 0) ? '#fff1f2' : 'inherit',
                        cursor: (r.sharesTransferAmount > 0) ? 'help' : 'default',
                      }}
                        title={(r.sharesTransferAmount > 0)
                          ? `Includes Ksh ${Number(r.sharesTransferAmount).toLocaleString()} in share capital transfer(s) received — NOT counted as a deduction. Regular contribution: Ksh ${(Number(r.sharesContribution) - Number(r.sharesTransferAmount)).toLocaleString()}. Total Deductions uses the regular contribution only.`
                          : undefined}
                      >
                        {(r.sharesTransferAmount > 0) ? (
                          <span style={{ color: '#dc2626', fontWeight: 700 }}>
                            {fmt(r.sharesContribution, 2)}
                            <span style={{ marginLeft: 5, fontSize: 10, background: '#dc2626', color: '#fff', borderRadius: 3, padding: '1px 4px', fontWeight: 700, verticalAlign: 'middle' }}>+transfer</span>
                          </span>
                        ) : fmt(r.sharesContribution, 2)}
                      </td>
                      <td className="text-right" style={{ paddingRight: 15 }}>{fmt(r.loanBalance, 2)}</td>
                      <td className="text-right" style={{ paddingRight: 15 }}>{fmt(r.loanInterest, 2)}</td>
                      <td className="text-right">{fmt(r.loanRepayment, 0)}</td>
                      <td className="text-right">{fmt(r.additionalNormalDisbursed || 0, 2)}</td>
                      <td className="text-right">{fmt(r.schoolFeesLoan, 2)}</td>
                      <td className="text-right">{fmt(r.schoolFeesInterest, 2)}</td>
                      <td className="text-right">{fmt(r.schoolFeesRepayment, 0)}</td>
                      <td className="text-right">{fmt(r.additionalSchoolDisbursed || 0, 2)}</td>
                      <td className="text-right">{fmt(r.emergencyLoan, 2)}</td>
                      <td className="text-right">{fmt(r.emergencyLoanInterest, 2)}</td>
                      <td className="text-right">{fmt(r.emergencyLoanRepayment, 0)}</td>
                      <td className="text-right">{fmt(r.additionalEmergencyDisbursed || 0, 2)}</td>
                      <td className="text-right">{fmt(r.applicationFees, 2)}</td>
                      <td className="text-right"><b>{fmt(r.totalDeductions, 2)}</b></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f7f7f7' }}>
                  <td style={{ paddingRight: 10 }}><b>TOTALS</b></td>
                  <td></td>
                  <td className="text-right" style={{ paddingRight: 15 }}><b>{fmt(data.summary.totalShares, 2)}</b></td>
                  <td className="text-right" style={{ paddingRight: 15 }}><b>{fmt(data.summary.totalSharesContributed, 0)}</b></td>
                  <td style={{ paddingRight: 15 }}></td>
                  <td className="text-right" style={{ paddingRight: 15 }}><b>{fmt(data.summary.totalLoanInterest, 2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalLoanRepayments, 0)}</b></td>
                  <td></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalSchoolInterest, 2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalSchoolRepayments, 0)}</b></td>
                  <td></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalEmergencyInterest, 2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.totalEmergencyRepayments, 0)}</b></td>
                  <td></td>
                  <td className="text-right"><b>{fmt(data.summary.totalApplicationFees, 2)}</b></td>
                  <td className="text-right"><b>{fmt(data.summary.grandTotalDeductions, 2)}</b></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

      </div>
    </div>
  );
}
